import { useCallback } from 'react'
import {
  useTrackProduct,
  trackProductClicked,
  trackProductViewed,
} from '@/features/shared/analytics/products'
import {
  type OnQtyCallback,
  type OnProductCallback,
} from '@/features/shop/components/ProductCard/types'
import { DISPLAY_FORMAT } from '@/features/shared/constants/analytics'
import { useStoreParams } from '@/features/shared/utils/dataFetching/storeParams'

type Props = ProductShelfTrackingProps & {
  shelfName: string
  shelfIndex: number
}

export type ProductShelfTrackingProps = {
  searchTerm?: string
  categoryName?: string
  categoryId?: number
  searchType?: string
  featuredTransactionID?: string
  adPlacementId?: string
  analyticSlug?: string
  shelfCategory?: string
  shelfCategoryIndex?: number
}

export const useOnShelfProduct = ({
  shelfName,
  shelfIndex,
  searchTerm,
  categoryId,
  categoryName,
  searchType,
  featuredTransactionID,
  adPlacementId,
  analyticSlug,
  shelfCategory,
  shelfCategoryIndex,
}: Props) => {
  const { trackProductAdded, trackProductRemoved } = useTrackProduct()
  const { store_id, metro_id, store_location_id } = useStoreParams()
  const getEventProperties = useCallback(() => {
    return {
      shelfIndex,
      subLocation: analyticSlug ?? shelfName,
      ...(shelfCategory && { shelfCategory }),
      ...(shelfCategoryIndex !== undefined && { shelfCategoryIndex }),
      displayFormat: DISPLAY_FORMAT.HORIZONTAL,
      ...(searchTerm && {
        searchTerm,
      }),
      ...(store_id &&
        metro_id &&
        store_location_id && {
          storeParams: { store_id, metro_id, store_location_id },
        }),
      ...(categoryId && {
        categoryId,
      }),
      ...(categoryName && {
        categoryName,
      }),
      ...(searchType && { searchType }),
      ...(featuredTransactionID && {
        featured_transaction_id: featuredTransactionID,
      }),
      ...(adPlacementId && {
        ad_placement_id: adPlacementId,
      }),
    }
  }, [
    shelfIndex,
    shelfName,
    searchTerm,
    store_id,
    metro_id,
    store_location_id,
    categoryId,
    categoryName,
    searchType,
    featuredTransactionID,
    adPlacementId,
    analyticSlug,
    shelfCategory,
    shelfCategoryIndex,
  ])

  const onQtyAdded: OnQtyCallback = useCallback(
    ({ index, product }) => {
      trackProductAdded({ ...getEventProperties(), product, listIndex: index })
    },
    [trackProductAdded, getEventProperties],
  )

  const onQtyRemoved: OnProductCallback = useCallback(
    ({ index, product }) => {
      trackProductRemoved({
        ...getEventProperties(),
        product,
        listIndex: index,
      })
    },
    [trackProductRemoved, getEventProperties],
  )

  const onProductClick: OnProductCallback = useCallback(
    ({ index, product }) => {
      trackProductClicked({
        ...getEventProperties(),
        product,
        listIndex: index,
      })
    },
    [getEventProperties],
  )

  const onProductView: OnProductCallback = useCallback(
    ({ index, product }) => {
      trackProductViewed({ ...getEventProperties(), product, listIndex: index })
    },
    [getEventProperties],
  )

  return {
    onQtyAdded,
    onQtyRemoved,
    onProductView,
    onProductClick,
  }
}
